import React, { memo } from 'react'
import { Link } from 'react-router-dom'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { makeStyles } from 'transfix-ui/core/styles'
import { Maybe } from 'types/graphqlTypes'
import { ConnectedPhoneNumber } from 'apps/marketplace/components/ConnectedPhoneNumber'
import { IUseShipperFacilityState } from 'apps/marketplace/hooks/useShipperFacility/useShipperFacility'
import { IUseShipperFacilityVendorsState } from 'apps/marketplace/hooks/useShipperFacilityVendors/useShipperFacilityVendors'

export interface IFacilityDetailsProps {
  shipperFacilityState: IUseShipperFacilityState
  shipperFacilityVendorsState: IUseShipperFacilityVendorsState
  selectedVendorId: Maybe<string>
  showScheduler?: boolean
}

const useFacilityDetailsStyles = makeStyles(() => ({
  notesContainer: {
    overflowWrap: 'break-word',
  },
}))

// Amount of characters to show in truncatedFacilityUuid
const TRUNCATED_FACILITY_UUID_LENGTH = 6

const FacilityDetails = memo<IFacilityDetailsProps>(
  ({ selectedVendorId, shipperFacilityState, shipperFacilityVendorsState, showScheduler }) => {
    const classes = useFacilityDetailsStyles()
    const {
      facilityCodes,
      facilityLocation,
      facilityName,
      facilityUuid = '',
      loading: loadingShipperFacility,
      scheduler,
      schedulingNotes,
    } = shipperFacilityState
    const {
      loaded: loadedShipperFacilityVendors,
      shipperFacilityVendorData,
    } = shipperFacilityVendorsState
    const selectedVendor =
      loadedShipperFacilityVendors && selectedVendorId
        ? shipperFacilityVendorData.find(item => item.vendorId === selectedVendorId)
        : null
    const selectedVendorAddress = selectedVendor
      ? selectedVendor.vendorShipperFacilityAddress || selectedVendor.vendorFacilityAddress
      : null

    const truncatedFacilityUuid = facilityUuid.substring(
      facilityUuid.length - TRUNCATED_FACILITY_UUID_LENGTH
    )

    // Location
    const { address, addressLine2, city, stateCode, zipcode } = facilityLocation || {}

    // Facility Scheduler
    const { email: schedulerEmail, name: schedulerName, phone, phoneExtension } = scheduler || {}

    return (
      <Box mt={2}>
        {!loadingShipperFacility && (
          <>
            <Box mb={2}>
              <Link to={`/facilities/${facilityUuid}`} target='_blank'>
                <div>{truncatedFacilityUuid}</div>
                <div>{facilityName}</div>
                {!!selectedVendor && <div>{selectedVendor.vendorName} (vendor)</div>}
              </Link>
            </Box>
            <Box mb={2}>
              <Typography variant='body2'>
                <div>{facilityCodes}</div>
                <div>
                  {address} {addressLine2}
                </div>
                {!!selectedVendorAddress && <div>{selectedVendorAddress}</div>}
                <div>
                  {city}, {stateCode} {zipcode}
                </div>
              </Typography>
            </Box>
          </>
        )}
        {showScheduler && scheduler && (
          <Box mb={2}>
            <Box mb={1}>
              <Typography variant='subtitle1'>Scheduling</Typography>
            </Box>
            <Typography variant='body2'>{schedulerName}</Typography>
            <Typography variant='body2' color='textSecondary'>
              <div>{schedulerEmail}</div>
              {phone && (
                <ConnectedPhoneNumber phoneNumber={phone} phoneExtension={phoneExtension || ''} />
              )}
            </Typography>
            {schedulingNotes && (
              <div>
                <Typography variant='body2'>Notes:</Typography>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  className={classes.notesContainer}>
                  {schedulingNotes}
                </Typography>
              </div>
            )}
          </Box>
        )}
      </Box>
    )
  }
)

export default FacilityDetails
